import * as React from 'react'
import { FunctionComponent, useState, useRef } from 'react'
import TutorialSection from '../onboarding-tutorial-section'
import OnboardingSteps from '../onboarding-steps'
import OnboardingNav from '../onboarding-nav/onboarding-nav'

interface TutorialProps {
  heading: string
  title: string
  overview: { part1?: string; part2?: string }
  coverPhoto: string
  sectionTitle: string
  tutorialSectionContent: any
  endSection: string
  isSetupTutorial?: boolean
}
const OnboardingTutorialContent: FunctionComponent<TutorialProps> = ({
  heading,
  title,
  overview,
  coverPhoto,
  sectionTitle,
  tutorialSectionContent,
  endSection,
  isSetupTutorial = false
}) => {
  const overviewRef = useRef(null)
  const layoutRef = useRef(null)
  const setDatesRef = useRef(null)
  const skipRef = useRef(null)
  const reorderRef = useRef(null)
  const resourceRef = useRef(null)
  const faqRef = useRef(null)
  const topRef = useRef(null)

  const tutorialNavItems2 = [
    {
      name: $localize`:onboarding.tutorialNav1|Onboarding Tutorial Nav1 - Tutorial section Nav@@onboardingTutorialNav1:Overview`,
      ref: overviewRef
    },
    {
      name: $localize`:onboarding.tutorialNav2|Onboarding Tutorial Nav2 - Tutorial section Nav2@@onboarding2TutorialNav2:Understanding the Page Layout`,
      ref: layoutRef
    },
    {
      name: $localize`:onboarding.tutorialNav3|Onboarding Tutorial Nav3 - Tutorial section Nav@@onboardingTutorialNav3:Set Your Dates`,
      ref: setDatesRef
    },
    {
      name: $localize`:onboarding.tutorialNav4|Onboarding Tutorial Nav4 - Tutorial section Nav@@onboardingTutorialNav4:Enable or Skip Sessions`,
      ref: skipRef
    },
    {
      name: $localize`:onboarding.tutorialNav5|Onboarding Tutorial Nav5 - Tutorial section Nav@@onboardingTutorialNav5:Reorder Sessions`,
      ref: reorderRef
    },
    {
      name: $localize`:onboarding.tutorialNav6|Onboarding Tutorial Nav6 - Tutorial section Nav@@onboardingTutorialNav6:Access Session Resources`,
      ref: resourceRef
    },
    {
      name: $localize`:onboarding.tutorialNav7|Onboarding Tutorial Nav7 - Tutorial section Nav@@onboardingTutorialNav7:Frequently Asked Questions`,
      ref: faqRef
    }
  ]
  const tutorialNavItems1 = [
    {
      name: $localize`:onboarding.tutorial1Nav1|Onboarding Tutorial1 Nav1 - Tutorial1 section Nav@@onboardingTutorialNav1:Overview`,
      ref: overviewRef
    },
    {
      name: $localize`:onboarding.tutorial1Nav2|Onboarding Tutorial1 Nav2 - Tutorial1 section Nav2@@onboardingTutorialNav2:Key Tips to keep in mind`,
      ref: faqRef
    }
  ]

  const steps = [
    {
      count: $localize`:onboarding.step1Count|Step label@@onboardingStep1Count:Step 1`,
      name: 'layout',
      ref: layoutRef,
      title: $localize`:onboarding.step1Title|Step 1 Title@@onboardingStep1Title:Understand the Page Layout`,
      coverPhoto: '../images/cards/sample_image.svg',
      desc: $localize`:onboarding.step1Desc|Step 1 Description@@onboardingStep1Desc:
      On the Schedule page, there are two sections: My Team Training Schedule and My Alpha Course Schedule.</br></br>
      Sessions under <b>My Team Training Schedule</b> are there to set your team up for running the most effective Alpha. We recommend doing this as a team two weeks before your Alpha course starts.</br></br>
      Sessions under <b>My Alpha Course Schedule</b> are all the Alpha Episodes presented in the recommended order but are fully customizable.`
    },
    {
      count: $localize`:onboarding.step2Count|Step label@@onboardingStep2Count:Step 2`,
      name: 'set-dates',
      ref: setDatesRef,
      title: $localize`:onboarding.step2Title|Step 2 Title@@onboardingStep2Title:Set Your Dates`,
      coverPhoto: '../images/cards/sample_image.svg',
      desc: $localize`:onboarding.step2Desc|Step 2 Description@@onboardingStep2Desc:
      Your sessions are pre-filled based on the start date you set during registration.`,
      desc2: $localize`:onboarding.step2Desc2|Step 2 Description2@@onboardingStep2Desc2:
      Click the <b>Calendar Icon</b> to pick a new date for any session.`,
      coverPhoto2: '../images/cards/sample_image.svg',
      desc3: $localize`:onboarding.step2Desc3|Step 2 Description3@@onboardingStep2Desc3:
      The <b>Holy Spirit Weekend/Day Away</b> sessions default to the same day, but you can move them to fit your schedule.`,
      coverPhoto3: '../images/cards/sample_image.svg'
    },

    {
      count: $localize`:onboarding.step3Count|Step label@@onboardingStep3Count:Step 3`,
      name: 'enable-skip',
      ref: skipRef,
      title: $localize`:onboarding.step3Title|Step 3 Title@@onboardingStep3Title:Enable or Skip Sessions`,
      coverPhoto: '../images/cards/sample_image.svg',
      desc: $localize`:onboarding.step3Desc|Step 3 Description@@onboardingStep3Desc:
      All sessions are switched <b>on</b> by default, but you can click on the <b>Toggle</b> to turn off any session you won’t be running.</br></br>
      Skipped sessions aren’t gone forever - you can switch them back on at any time.`
    },
    {
      count: $localize`:onboarding.step4Count|Step label@@onboardingStep4Count:Step 4`,
      name: 'reorder',
      ref: reorderRef,
      title: $localize`:onboarding.step4Title|Step 4 Title@@onboardingStep4Title:Reorder Sessions`,
      coverPhoto: '../images/cards/sample_image.svg',
      desc: $localize`:onboarding.step4Desc|Step 4 Description@@onboardingStep4Desc:
      Want to change the order? Click on the <b>Arrows Icon</b> and drag a session to rearrange it.</br></br>
      By default, your Alpha sessions are in our recommended order. 
      The first six sessions are foundational. 
      They are designed to be watched in order, each building on the last, 
      setting your group up for the best Alpha experience. So, we'd highly recommend you leave them as they are.`
    },
    {
      count: $localize`:onboarding.step5Count|Step label@@onboardingStep5Count:Step 5`,
      name: 'resources',
      ref: resourceRef,
      title: $localize`:onboarding.step5Title|Step 5 Title@@onboardingStep5Title:Access Session Resources`,
      coverPhoto: '../images/cards/sample_image.svg',
      desc: $localize`:onboarding.step5Desc|Step 5 Description@@onboardingStep5Desc:
      Click on a video or episode title to preview the video, download it, and access all related resources for that session.`
    }
  ]

  return (
    <div
      ref={topRef}
      className='flex flex-row justify-between gap-8 md:gap-8 h-auto box-border justify-items-stretch'
    >
      <div className='flex flex-col justify-start gap-x-10 flex-[2] w-full'>
        <small className='text-alpha-black-40'>{heading}</small>
        <h1 className='leading-none text-alpha-black font-alpha-family text-[40px]'>
          {title}
        </h1>
        <img
          src={coverPhoto}
          className='relative border w-full rounded-lg flex bg-cover bg-no-repeat bg-center box-border'
        />
        <div id='overview' className='pt-4'>
          <h4 ref={overviewRef} className='leading-none text-alpha-black'>
            Overview
          </h4>
        </div>
        <p className='text-base'>{overview.part1}</p>
        {overview.part2 != null ? (
          <p className='text-base'>
            <br />
            {overview.part2}
          </p>
        ) : (
          ''
        )}
        <hr className='mb-[1.5rem] mt-[1.5rem]' />
        {isSetupTutorial ? <OnboardingSteps steps={steps} /> : null}
        <h4 className='leading-none text-alpha-black'>{sectionTitle}</h4>
        <div id='faq'>
          <div ref={faqRef}>
            {tutorialSectionContent.map((item, index) => (
              <TutorialSection
                key={index}
                title={item.title}
                text={item.text}
              />
            ))}
          </div>
        </div>
        <p className='text-base'>{endSection}</p>
        {/*</div>*/}
      </div>
      <div className='hidden md:block flex-1 '>
        <div className='sticky top-10'>
          <OnboardingNav
            topRef={topRef}
            offsetMargin={
              isSetupTutorial ? '0px 0px -60% 0px' : '-40% 0px 0px 0px'
            }
            menuItems={isSetupTutorial ? tutorialNavItems2 : tutorialNavItems1}
          />
        </div>
      </div>
    </div>
  )
}

export default OnboardingTutorialContent
